import type { FieldDefinition } from "@estelle/domain";

import type { Projection } from "@/modules/types";

/**
 * One CSV cell, escaped.
 *
 * A field is quoted only when it has to be — a comma, a quote, a newline, or
 * leading/trailing space that a reader would otherwise trim. Quoting
 * everything is also correct and makes the file unreadable in a terminal,
 * which is where most of these get looked at first.
 */
function cell(value: unknown): string {
  const text = toText(value);
  const needsQuotes = /[",\r\n]/.test(text) || text !== text.trim();
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * A projection value is `unknown`, and only some shapes have a textual form
 * worth exporting. An array joins on a semicolon rather than a comma so a
 * tag list does not silently become two columns.
 */
function toText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(toText).join("; ");
  return "";
}

/**
 * Rows as CSV, in the column order the user chose.
 *
 * Values are the raw projection, not what the table rendered. A spreadsheet
 * wants `1299.00` and `2026-09-08T04:00:00.000Z`, not `₹1,299.00` and
 * `08 Sep 2026` — the first two sort and calculate, the second two are
 * strings that look like numbers and dates.
 *
 * CRLF and a UTF-8 BOM, because the overwhelming majority of these files are
 * opened in Excel: without the BOM it renders `₹` and every Indian name as
 * mojibake, and without CRLF older versions run the rows together.
 */
export function toCsv(
  fields: FieldDefinition[],
  rows: readonly Projection[],
): string {
  const header = fields.map((field) => cell(field.label)).join(",");
  const body = rows.map((row) =>
    fields.map((field) => cell(row[field.name])).join(","),
  );
  // Escaped rather than literal: a raw BOM in source is invisible, and
  // ESLint rejects it as irregular whitespace for exactly that reason.
  const BOM = "\ufeff";
  return `${BOM}${[header, ...body].join("\r\n")}\r\n`;
}

/**
 * A filename someone can find again.
 *
 * `orders-all-2026-09-09.csv`. The date is the export's, taken from the
 * caller rather than the clock, so a fixture-backed export is as reproducible
 * as the data in it.
 */
export function csvFilename(
  scope: string,
  section: string,
  isoDate: string,
): string {
  const safe = (part: string) =>
    part
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  return `${safe(scope)}-${safe(section)}-${isoDate.slice(0, 10)}.csv`;
}

/**
 * Hand the file to the browser.
 *
 * A blob and an object URL rather than a `data:` URI: Safari caps the latter
 * at a couple of megabytes, and a year of invoice lines passes that.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoked on the next tick rather than immediately: Firefox has been known
  // to cancel a download whose URL is revoked in the same frame as the click.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
